import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface VotingState {
  sessions: Record<string, {
    id: string;
    projectId: string;
    type: string;
    status: string;
    results: any;
    userVote: any | null;
    voteDetails?: any[];
    isLoading: boolean;
  }>;
  casting: Record<string, boolean>;
  error: string | null;
}

const initialState: VotingState = {
  sessions: {},
  casting: {},
  error: null,
};

export const fetchSession = createAsyncThunk(
  'voting/fetchSession',
  async ({ projectId, sessionId }: { projectId: string; sessionId: string }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
      : {};
    const response = await axios.get(
      `${API_BASE_URL}/votes/projects/${projectId}/sessions/${sessionId}/results`,
      config
    );
    return { sessionId, data: response.data.data.session };
  }
);

export const castVote = createAsyncThunk(
  'voting/castVote',
  async (
    { projectId, sessionId, option, geographicData }: {
      projectId: string;
      sessionId: string;
      option: string;
      geographicData?: any;
    },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(
      `${API_BASE_URL}/votes/projects/${projectId}/sessions/${sessionId}/cast`,
      { option, geographicData },
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return { sessionId, vote: response.data.data.vote, results: response.data.data.results };
  }
);

const votingSlice = createSlice({
  name: 'voting',
  initialState,
  reducers: {
    updateResults: (state, action: PayloadAction<{ sessionId: string; results: any }>) => {
      if (state.sessions[action.payload.sessionId]) {
        state.sessions[action.payload.sessionId].results = action.payload.results;
      }
    },
    setUserVote: (state, action: PayloadAction<{ sessionId: string; vote: any }>) => {
      if (state.sessions[action.payload.sessionId]) {
        state.sessions[action.payload.sessionId].userVote = action.payload.vote;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.sessions[action.payload.sessionId] = {
          id: action.payload.sessionId,
          projectId: action.payload.data.projectId || '',
          type: action.payload.data.type,
          status: action.payload.data.status,
          results: action.payload.data.results,
          userVote: action.payload.data.userVote || null,
          voteDetails: action.payload.data.voteDetails,
          isLoading: false,
        };
      })
      .addCase(castVote.pending, (state, action) => {
        state.casting[action.meta.arg.sessionId] = true;
      })
      .addCase(castVote.fulfilled, (state, action) => {
        state.casting[action.payload.sessionId] = false;
        if (state.sessions[action.payload.sessionId]) {
          state.sessions[action.payload.sessionId].userVote = action.payload.vote;
          state.sessions[action.payload.sessionId].results = action.payload.results;
        }
      })
      .addCase(castVote.rejected, (state, action) => {
        state.casting[action.meta.arg.sessionId] = false;
        state.error = action.error.message || 'Failed to cast vote';
      });
  },
});

export const { updateResults, setUserVote } = votingSlice.actions;
export default votingSlice.reducer;

